/**
 * Bottom scroll (书卷) dialogue box after style-anchors/dialogue-box-concept.png: nine-slice parchment
 * panel with lacquer border + gold line, gold wave-and-knot ornament top and bottom, rolled knobs at
 * both ends, 96x96 portrait in a lacquered frame at the left, speaker plate, punctuation-aware
 * typewriter (textSpeed setting, Shift/Ctrl `input:fastText` accelerates), a tiny lacquer continue
 * arrow, choice list, scroll-back history (H) and tap-anywhere-to-advance.
 * Implements IDialogueBox for the dialogue runner.
 */
import Phaser from 'phaser';
import type { IDialogueBox } from '@/core/types/contracts';
import { bus, Events } from '@/core/events';
import { FONT_BODY, GAME_H, GAME_W } from '@/core/config';
import { audio } from '@/audio/index';
import { ACTOR_DEFS } from '@/characters/index';
import type { UiRoot } from './UiRoot';
import { makeMeasurer, uiText } from './text';
import { Css, Layout, Slices, UiDepth, UiTex } from './theme';
import { Typewriter, type TextSpeed } from './typewriter';
import { EMOTIONS, emotionChain, frameNameFor } from './emotions';
import { wrapText } from './wrap';
import { MenuList } from './MenuList';

export interface DialogueLine { speaker?: string; portrait?: string; emotion?: string; text: string }
export interface HistoryEntry { speaker?: string; text: string }
/** Texture (+ atlas frame) a portrait resolved to. */
export interface PortraitRef { key: string; frame?: string }

const BODY_STYLE: Phaser.Types.GameObjects.Text.TextStyle = { fontFamily: FONT_BODY, fontSize: `${Layout.bodyFontPx}px`, color: Css.ink, lineSpacing: 5 };
const HISTORY_MAX = 200;
const HISTORY_VISIBLE_LINES = 10;
/** Typewriter time multiplier while input:fastText is held. */
export const FAST_TEXT_MULTIPLIER = 5;

export class DialogueBox implements IDialogueBox {
  readonly container: Phaser.GameObjects.Container;
  private readonly panel: Phaser.GameObjects.NineSlice;
  private readonly waveTop: Phaser.GameObjects.TileSprite;
  private readonly waveBottom: Phaser.GameObjects.TileSprite;
  private readonly knobL: Phaser.GameObjects.Image;
  private readonly knobR: Phaser.GameObjects.Image;
  private readonly portraitFrame: Phaser.GameObjects.Image;
  private readonly portrait: Phaser.GameObjects.Image;
  private readonly namePlate: Phaser.GameObjects.NineSlice;
  private readonly nameText: Phaser.GameObjects.Text;
  private readonly body: Phaser.GameObjects.Text;
  private readonly cursor: Phaser.GameObjects.Image;
  private readonly historyHint: Phaser.GameObjects.Text;
  private readonly tapZone: Phaser.GameObjects.Zone;
  private readonly measurer: ReturnType<typeof makeMeasurer>;

  private typer: Typewriter | null = null;
  private typingResolve: (() => void) | null = null;
  private advanceWaiters: (() => void)[] = [];
  private readonly history: HistoryEntry[] = [];
  private choiceMenu: MenuList | null = null;
  private choicePanel: Phaser.GameObjects.Container | null = null;
  private choiceResolve: ((i: number) => void) | null = null;
  private historyView: { container: Phaser.GameObjects.Container; text: Phaser.GameObjects.Text; lines: string[]; offset: number; dispose: () => void } | null = null;
  private cursorTween: Phaser.Tweens.Tween | null = null;
  private fastHeld = false;
  private _visible = false;
  private portraitRef: PortraitRef | null = null;

  constructor(private readonly root: UiRoot) {
    const s = root.scene;
    const L = Layout.dialogue;
    this.container = s.add.container(0, 0).setDepth(UiDepth.DIALOGUE).setVisible(false).setAlpha(0);

    this.panel = s.add.nineslice(L.x, L.y, Slices.scroll.key, undefined, L.w, L.h, Slices.scroll.l, Slices.scroll.r, Slices.scroll.t, Slices.scroll.b).setOrigin(0);
    const waveX = L.x + L.knob + 8, waveW = L.w - (L.knob + 8) * 2;
    this.waveTop = s.add.tileSprite(waveX, L.y + 5, waveW, 6, UiTex.waveStrip).setOrigin(0);
    this.waveBottom = s.add.tileSprite(waveX, L.y + L.h - 11, waveW, 6, UiTex.waveStrip).setOrigin(0).setFlipY(true);
    this.knobL = s.add.image(L.x - 2, L.y - 4, UiTex.scrollKnob).setOrigin(0);
    this.knobR = s.add.image(L.x + L.w + 2, L.y - 4, UiTex.scrollKnob).setOrigin(1, 0).setFlipX(true);

    const P = Layout.portrait;
    this.portraitFrame = s.add.image(P.x, P.y, UiTex.portraitFrame).setOrigin(0);
    this.portrait = s.add.image(P.x + 4, P.y + 4, UiTex.portraitMissing).setOrigin(0).setDisplaySize(P.size, P.size);

    this.namePlate = s.add.nineslice(this.bodyX(true), L.y - 8, Slices.bar.key, undefined, 96, 20, Slices.bar.l, Slices.bar.r, Slices.bar.t, Slices.bar.b).setOrigin(0);
    this.nameText = uiText(s, this.bodyX(true) + 12, L.y + 2, '', { fontSize: '14px', fontStyle: 'bold', color: Css.parchment }).setOrigin(0, 0.5);

    this.body = uiText(s, this.bodyX(true), L.y + 18, '', BODY_STYLE);
    this.cursor = s.add.image(this.cursorX(), this.cursorY(), UiTex.arrowDown).setOrigin(0.5).setVisible(false);
    this.historyHint = uiText(s, L.x + L.w - L.knob - 10, L.y + 9, 'H 记录', { fontSize: '10px', color: Css.inkSoft }).setOrigin(1, 0);
    const hintZone = s.add.zone(L.x + L.w - L.knob - 10, L.y + 9, 44, 14).setOrigin(1, 0).setInteractive({ useHandCursor: true });
    hintZone.on('pointerdown', (_p: Phaser.Input.Pointer, _x: number, _y: number, ev: { stopPropagation?: () => void }) => { ev.stopPropagation?.(); this.toggleHistory(); });

    this.tapZone = s.add.zone(L.x, L.y - 12, L.w, L.h + 12).setOrigin(0).setInteractive();
    this.tapZone.on('pointerdown', (p: Phaser.Input.Pointer) => this.onTap(p));

    this.container.add([this.tapZone, this.panel, this.waveTop, this.waveBottom, this.knobL, this.knobR, this.portraitFrame, this.portrait, this.namePlate, this.nameText, this.body, this.cursor, this.historyHint, hintZone]);
    this.measurer = makeMeasurer(s, BODY_STYLE);

    root.onUpdate((_t, delta) => this.tick(delta));
    root.onBus(Events.INPUT_CONFIRM, () => this.onConfirm());
    root.onBus(Events.INPUT_FAST_TEXT, (p) => { this.fastHeld = Boolean(p?.held); });
    root.onKey('H', () => { if (this._visible || this.historyView) this.toggleHistory(); });
    root.track(() => this.teardown());
  }

  // ------------------------------------------------------------------ IDialogueBox
  get visible(): boolean { return this._visible; }

  show(line: DialogueLine, opts: { instant: boolean }): Promise<void> {
    this.resolveTyping();
    const s = this.root.scene;
    const L = Layout.dialogue;
    const hasPortrait = Boolean(line.speaker || line.portrait);
    this.portraitFrame.setVisible(hasPortrait);
    this.portrait.setVisible(hasPortrait);
    if (hasPortrait) this.applyPortrait(this.resolvePortrait(line));
    else this.portraitRef = null;

    const speakerName = line.speaker ? (ACTOR_DEFS[line.speaker]?.displayName ?? line.speaker) : '';
    this.namePlate.setVisible(Boolean(speakerName));
    this.nameText.setVisible(Boolean(speakerName)).setText(speakerName);
    const bodyX = this.bodyX(hasPortrait);
    this.namePlate.setPosition(bodyX - 2, L.y - 8).setSize(Math.max(64, Math.round(this.nameText.width) + 24), 20);
    this.nameText.setPosition(bodyX + 10, L.y + 2);

    const bodyW = L.x + L.w - L.knob - L.pad - 6 - bodyX;
    this.body.setPosition(bodyX, L.y + 18);
    const lines = wrapText(line.text, bodyW, this.measurer.measure);
    const speed: TextSpeed = opts.instant ? 'instant' : (this.root.ctx?.settings.textSpeed ?? 'normal');
    this.typer = new Typewriter(lines.join('\n'), speed);
    this.body.setText(this.typer.text);
    this.setCursor(false);

    if (!this._visible) {
      this._visible = true;
      this.container.setVisible(true);
      s.tweens.killTweensOf(this.container);
      s.tweens.add({ targets: this.container, alpha: 1, duration: this.root.reduceMotion() ? 0 : 120 });
    }
    return new Promise<void>((resolve) => {
      this.typingResolve = resolve;
      if (this.typer?.done) this.finishTyping();
    });
  }

  completeTyping(): void {
    if (this.typer && !this.typer.done) { this.typer.complete(); this.finishTyping(); }
  }

  isTyping(): boolean { return this.typer !== null && !this.typer.done; }

  hide(): Promise<void> {
    this.resolveTyping();
    this.typer = null;
    this.setCursor(false);
    this.closeChoices(-1);
    if (!this._visible) return Promise.resolve();
    this._visible = false;
    const s = this.root.scene;
    return new Promise((resolve) => {
      s.tweens.killTweensOf(this.container);
      s.tweens.add({ targets: this.container, alpha: 0, duration: this.root.reduceMotion() ? 0 : 140, onComplete: () => { if (!this._visible) this.container.setVisible(false); resolve(); } });
    });
  }

  showChoices(choices: { text: string }[]): Promise<number> {
    this.closeChoices(-1);
    this.completeTyping();
    this.setCursor(false);
    const s = this.root.scene;
    const rowH = 24;
    const w = 280;
    const h = choices.length * rowH + 20;
    const x = GAME_W - 6 - w;
    const y = Layout.dialogue.y - 14 - h;
    const panel = s.add.container(0, 0).setDepth(UiDepth.CHOICES);
    const bg = s.add.nineslice(x, y, Slices.scroll.key, undefined, w, h, Slices.scroll.l, Slices.scroll.r, Slices.scroll.t, Slices.scroll.b).setOrigin(0);
    panel.add(bg);
    this.choicePanel = panel;
    this.root.pushModal('choices');
    audio().sfx('ui-open');
    return new Promise<number>((resolve) => {
      this.choiceMenu = new MenuList(s, {
        x: x + w / 2, y: y + 10 + rowH / 2, width: w - 24, rowHeight: rowH, fontSize: 15, align: 'left',
        color: Css.ink, selectedColor: Css.parchment,
        items: choices.map((c, i) => ({ label: c.text, onSelect: () => this.closeChoices(i) })),
        isBlocked: () => this.root.modal !== 'choices',
        depth: UiDepth.CHOICES + 1,
      });
      this.choiceResolve = resolve;
    });
  }

  pushHistory(speaker: string | undefined, text: string): void {
    const entry: HistoryEntry = { text };
    if (speaker) entry.speaker = ACTOR_DEFS[speaker]?.displayName ?? speaker;
    this.history.push(entry);
    if (this.history.length > HISTORY_MAX) this.history.splice(0, this.history.length - HISTORY_MAX);
  }

  // ------------------------------------------------------------------ extras used by runners/showcase
  /** Resolves on the next confirm (key, tap or touch) once the current line is fully typed. */
  waitForAdvance(): Promise<void> {
    return new Promise((resolve) => this.advanceWaiters.push(resolve));
  }
  getHistory(): readonly HistoryEntry[] { return this.history; }
  clearHistory(): void { this.history.length = 0; }
  isHistoryOpen(): boolean { return this.historyView !== null; }
  isChoicesOpen(): boolean { return this.choiceMenu !== null; }
  isFastText(): boolean { return this.fastHeld; }
  /** Texture / atlas frame the current line's portrait resolved to (null while no portrait is shown). */
  currentPortrait(): PortraitRef | null { return this.portraitRef; }
  /** Currently displayed (possibly partial) body text. */
  get bodyText(): string { return this.body.text; }

  toggleHistory(): void { if (this.historyView) this.closeHistory(); else this.openHistory(); }

  openHistory(): void {
    if (this.historyView) return;
    const s = this.root.scene;
    const w = 520, h = 300;
    const x = (GAME_W - w) / 2, y = (GAME_H - h) / 2 - 10;
    const container = s.add.container(0, 0).setDepth(UiDepth.HISTORY);
    const scrim = s.add.rectangle(0, 0, GAME_W, GAME_H, 0x000000, 0.55).setOrigin(0).setInteractive();
    const paper = s.add.nineslice(x, y, Slices.paper.key, undefined, w, h, Slices.paper.l, Slices.paper.r, Slices.paper.t, Slices.paper.b).setOrigin(0);
    const title = uiText(s, x + w / 2, y + 16, '对话记录', { fontSize: '15px', fontStyle: 'bold', color: Css.lacquer }).setOrigin(0.5);
    const lineStyle: Phaser.Types.GameObjects.Text.TextStyle = { fontSize: '13px', color: Css.ink, lineSpacing: 4 };
    const measure = makeMeasurer(s, lineStyle);
    const lines: string[] = [];
    for (const e of this.history) {
      const prefix = e.speaker ? `【${e.speaker}】` : '　';
      for (const l of wrapText(prefix + e.text, w - 48, measure.measure)) lines.push(l);
      lines.push('');
    }
    if (lines.length === 0) lines.push('（尚无记录）');
    const text = uiText(s, x + 24, y + 36, '', lineStyle);
    const hint = uiText(s, x + w / 2, y + h - 14, '↑↓ 滚动 · H / Esc 关闭', { fontSize: '11px', color: Css.inkSoft }).setOrigin(0.5);
    const mk = (bx: number, by: number, label: string, fn: () => void) => {
      const bar = s.add.nineslice(bx, by, Slices.bar.key, undefined, 44, 18, Slices.bar.l, Slices.bar.r, Slices.bar.t, Slices.bar.b).setOrigin(0.5).setInteractive({ useHandCursor: true });
      const t = uiText(s, bx, by, label, { fontSize: '11px', color: Css.parchment }).setOrigin(0.5);
      bar.on('pointerdown', fn);
      return [bar, t];
    };
    container.add([scrim, paper, title, text, hint, ...mk(x + w - 40, y + 16, '关闭', () => this.closeHistory()), ...mk(x + 40, y + 16, '▲', () => scroll(-3)), ...mk(x + 88, y + 16, '▼', () => scroll(3))]);
    const view = { container, text, lines, offset: Math.max(0, lines.length - HISTORY_VISIBLE_LINES), dispose: () => { /* set below */ } };
    const render = () => { text.setText(view.lines.slice(view.offset, view.offset + HISTORY_VISIBLE_LINES).join('\n')); };
    const scroll = (d: number) => { view.offset = Phaser.Math.Clamp(view.offset + d, 0, Math.max(0, view.lines.length - HISTORY_VISIBLE_LINES)); render(); };
    render();
    const kb = s.input.keyboard;
    const keys: [string, () => void][] = [
      ['keydown-UP', () => scroll(-1)], ['keydown-W', () => scroll(-1)], ['keydown-DOWN', () => scroll(1)], ['keydown-S', () => scroll(1)],
      ['keydown-PAGE_UP', () => scroll(-8)], ['keydown-PAGE_DOWN', () => scroll(8)],
      ['keydown-ESC', () => this.closeHistory()], ['keydown-X', () => this.closeHistory()],
    ];
    for (const [k, fn] of keys) kb?.on(k, fn);
    const onWheel = (_p: unknown, _o: unknown, _dx: number, dy: number) => scroll(dy > 0 ? 2 : -2);
    s.input.on('wheel', onWheel);
    view.dispose = () => { for (const [k, fn] of keys) kb?.off(k, fn); s.input.off('wheel', onWheel); measure.destroy(); container.destroy(true); };
    this.historyView = view;
    this.root.pushModal('history');
    audio().sfx('ui-open');
  }

  closeHistory(): void {
    if (!this.historyView) return;
    this.historyView.dispose();
    this.historyView = null;
    this.root.popModal('history');
    audio().sfx('ui-cancel');
  }

  // ------------------------------------------------------------------ internals
  private bodyX(hasPortrait: boolean): number {
    const L = Layout.dialogue, P = Layout.portrait;
    return hasPortrait ? P.x + P.frame + 10 : L.x + L.knob + L.pad + 6;
  }
  private cursorX(): number { const L = Layout.dialogue; return L.x + L.w - L.knob - 18; }
  private cursorY(): number { const L = Layout.dialogue; return L.y + L.h - 20; }

  /**
   * Portrait lookup: texture bases from the line (`portrait`, `portrait-<portrait>`, the actor's
   * `portrait` key, `portrait-<speaker>`), then the emotion chain (raw → alias → … → neutral) against
   * the atlas frames of that base (or a legacy standalone `<base>-<emotion>` image), then any
   * standalone emotion image of that base, then the placeholder. Never warns.
   */
  private resolvePortrait(line: DialogueLine): PortraitRef {
    const tex = this.root.scene.textures;
    const bases: string[] = [];
    const push = (k: string | undefined) => { if (k && !bases.includes(k)) bases.push(k); };
    if (line.portrait) { push(line.portrait); push(`portrait-${line.portrait}`); }
    if (line.speaker) { push(ACTOR_DEFS[line.speaker]?.portrait); push(`portrait-${line.speaker}`); }
    const chain = emotionChain(line.emotion);
    for (const base of bases) {
      const frames = tex.exists(base) ? tex.get(base).getFrameNames() : [];
      for (const cand of chain) {
        const frame = frameNameFor(cand, frames);
        if (frame) return { key: base, frame };
        if (tex.exists(`${base}-${cand}`)) return { key: `${base}-${cand}` };
      }
      if (tex.exists(base)) { const first = frames[0]; return first !== undefined ? { key: base, frame: first } : { key: base }; }
      // Standalone set without a neutral image (current hero exports): any canonical emotion beats the placeholder.
      for (const e of EMOTIONS) if (tex.exists(`${base}-${e}`)) return { key: `${base}-${e}` };
    }
    return { key: UiTex.portraitMissing };
  }

  /** Swaps the portrait texture/frame; atlas frames may carry custom pivots, so the origin is pinned afterwards. */
  private applyPortrait(ref: PortraitRef): void {
    this.portraitRef = ref;
    const P = Layout.portrait;
    if (ref.frame !== undefined) this.portrait.setTexture(ref.key, ref.frame); else this.portrait.setTexture(ref.key);
    this.portrait.setOrigin(0, 0).setPosition(P.x + 4, P.y + 4).setDisplaySize(P.size, P.size);
  }

  private tick(delta: number): void {
    if (!this.typer || this.typer.done) return;
    if (this.root.modal === 'pause' || this.root.modal === 'settings' || this.historyView) return;
    const dt = Math.min(delta, 100) * (this.fastHeld ? FAST_TEXT_MULTIPLIER : 1);
    if (this.typer.tick(dt)) {
      this.body.setText(this.typer.text);
      if (this.typer.done) this.finishTyping();
    }
  }

  private finishTyping(): void {
    if (this.typer) this.body.setText(this.typer.text);
    this.setCursor(true);
    this.resolveTyping();
  }

  private resolveTyping(): void {
    const r = this.typingResolve;
    this.typingResolve = null;
    r?.();
  }

  private setCursor(on: boolean): void {
    const s = this.root.scene;
    if (this.cursorTween) { this.cursorTween.stop(); this.cursorTween.destroy(); this.cursorTween = null; }
    this.cursor.setPosition(this.cursorX(), this.cursorY()).setVisible(on);
    if (on && !this.root.reduceMotion()) {
      this.cursorTween = s.tweens.add({ targets: this.cursor, y: '-=3', duration: 380, yoyo: true, repeat: -1, ease: 'Sine.easeInOut' });
    }
  }

  private onTap(p: Phaser.Input.Pointer): void {
    if (!this._visible || this.historyView || this.choiceMenu || this.root.modal) return;
    if (this.isTyping()) { this.completeTyping(); return; }
    bus.emitTyped(Events.INPUT_CONFIRM, { source: p.wasTouch ? 'touch' : 'pointer' });
  }

  private onConfirm(): void {
    if (!this._visible || this.historyView || this.choiceMenu || this.root.modal) return;
    if (this.isTyping()) { this.completeTyping(); return; }
    const waiters = this.advanceWaiters.splice(0);
    for (const w of waiters) w();
  }

  private closeChoices(index: number): void {
    const menu = this.choiceMenu;
    const panel = this.choicePanel;
    const resolve = this.choiceResolve;
    this.choiceMenu = null; this.choicePanel = null; this.choiceResolve = null;
    if (menu) { menu.destroy(); this.root.popModal('choices'); }
    panel?.destroy(true);
    if (resolve) resolve(index < 0 ? 0 : index);
  }

  private teardown(): void {
    this.resolveTyping();
    this.closeChoices(-1);
    this.closeHistory();
    this.advanceWaiters.length = 0;
    this.measurer.destroy();
    if (this.cursorTween) { this.cursorTween.stop(); this.cursorTween = null; }
    this.container.destroy(true);
  }
}
